import { useEffect, useState } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Card, Badge, Button } from '@shared/components/ui';
import { getLogLevelTone, toneToBadgeVariant } from '@shared/config/theme';
import { RotateCcw, ChevronLeft, ChevronRight, Terminal, AlertCircle } from 'lucide-react';

interface SystemLog {
  id: string;
  level: string;
  source: string;
  message: string;
  created_at: string;
}

function translateKnownMessage(message: string, t: TFunction<'common'>, language: string) {
  const heartbeatPrefix = 'Agent heartbeat received from ';
  if (message.startsWith(heartbeatPrefix)) {
    return t('systemLogs.messages.agentHeartbeatReceived', {
      agentName: message.slice(heartbeatPrefix.length),
    });
  }

  const quotaMatch = message.match(
    /^Backup skipped: Storage quota exceeded \((\d+(?:\.\d+)?) GB used of (\d+(?:\.\d+)?) GB\)$/
  );
  if (quotaMatch) {
    const formatGigabytes = (value: string) =>
      new Intl.NumberFormat(language, { maximumFractionDigits: 2 }).format(Number(value));

    return t('systemLogs.messages.backupSkippedQuotaExceeded', {
      used: formatGigabytes(quotaMatch[1]),
      limit: formatGigabytes(quotaMatch[2]),
    });
  }

  return message;
}

export default function SystemLogs() {
  const { t, i18n } = useTranslation('common');
  const [logs, setLogs] = useState<SystemLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [levelFilter, setLevelFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [submittedQuery, setSubmittedQuery] = useState('');

  const formatTimestamp = (value: string) => {
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? t('systemLogs.unknownTime')
      : date.toLocaleString(i18n.language);
  };

  useEffect(() => {
    fetchLogs();
  }, [page, levelFilter, submittedQuery]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      let url = `/api/v1/logs/system?page=${page}&page_size=50`;
      if (levelFilter) url += `&level=${levelFilter}`;
      if (submittedQuery) url += `&search=${encodeURIComponent(submittedQuery)}`;

      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`System logs request failed: ${res.status}`);
      const data = await res.json();
      setLogs(data.logs || []);
      setTotalPages(Math.max(1, Math.ceil((data.count || 0) / 50)));
      setLoadError(false);
    } catch {
      setLogs([]);
      setTotalPages(1);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="space-y-8 animate-in fade-in duration-700">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-text-primary-light dark:text-text-primary-dark tracking-tight">
              {t('systemLogs.title')}
            </h1>
            <p className="text-text-muted-light dark:text-text-muted-dark font-medium mt-1">
              {t('systemLogs.description')}
            </p>
          </div>
          <Button
            variant="secondary"
            onClick={() => fetchLogs()}
            className="min-h-11 w-full md:w-auto"
            disabled={loading}
          >
            <RotateCcw
              aria-hidden="true"
              className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`}
            />{' '}
            {t('systemLogs.refresh')}
          </Button>
        </div>

        {/* Console-style Filter Bar */}
        <Card className="p-4">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setPage(1);
              setSubmittedQuery(searchQuery.trim());
            }}
            className="flex flex-col md:flex-row gap-4"
          >
            <div className="flex-1 relative">
              <Terminal
                size={16}
                aria-hidden="true"
                className="absolute left-3.5 top-3.5 text-text-muted-light dark:text-text-muted-dark"
              />
              <input
                id="system-logs-search"
                type="text"
                aria-label={t('systemLogs.search')}
                placeholder={t('systemLogs.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="min-h-11 w-full pl-10 pr-4 py-2.5 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl text-sm font-mono text-text-primary-light dark:text-text-primary-dark placeholder:text-text-subtle-light dark:placeholder:text-text-subtle-dark focus:outline-none focus:ring-2 focus:ring-brand-500 dark:focus:ring-brand-400 transition-all"
              />
            </div>
            <select
              aria-label={t('systemLogs.severity')}
              value={levelFilter}
              onChange={(e) => {
                setPage(1);
                setLevelFilter(e.target.value);
              }}
              className="min-h-11 px-4 py-2.5 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl text-xs font-bold uppercase tracking-widest text-text-primary-light dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-brand-400 cursor-pointer appearance-none min-w-[140px]"
            >
              <option value="">{t('systemLogs.allSeverities')}</option>
              <option value="ERROR">{t('systemLogs.errors')}</option>
              <option value="WARN">{t('systemLogs.warnings')}</option>
              <option value="INFO">{t('systemLogs.informational')}</option>
              <option value="DEBUG">{t('systemLogs.debug')}</option>
            </select>
            <Button
              type="submit"
              className="min-h-11 w-full md:w-auto bg-brand-600"
              disabled={loading}
            >
              {t('actions.search')}
            </Button>
          </form>
        </Card>

        {loading ? (
          <div className="py-20 flex justify-center" role="status" aria-live="polite">
            <span className="sr-only">{t('systemLogs.loading')}</span>
            <div className="animate-spin h-12 w-12 border-b-2 border-brand-600 rounded-full"></div>
          </div>
        ) : loadError ? (
          <div role="alert">
            <Card className="flex flex-col items-start gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3 text-state-danger-light dark:text-state-danger-dark">
                <AlertCircle aria-hidden="true" size={18} className="mt-0.5 shrink-0" />
                <p>{t('systemLogs.loadError')}</p>
              </div>
              <Button onClick={() => fetchLogs()} className="min-h-11 w-full sm:w-auto">
                {t('systemLogs.retry')}
              </Button>
            </Card>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="hidden lg:block bg-surface-card-light dark:bg-surface-card-dark rounded-2xl border border-surface-border-light dark:border-surface-border-dark overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-surface-border-light dark:border-surface-border-dark bg-surface-2-light dark:bg-surface-2-dark">
                      <th className="p-4 text-xs font-bold uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark">
                        {t('systemLogs.timestamp')}
                      </th>
                      <th className="p-4 text-xs font-bold uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark">
                        {t('systemLogs.level')}
                      </th>
                      <th className="p-4 text-xs font-bold uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark">
                        {t('systemLogs.source')}
                      </th>
                      <th className="p-4 text-xs font-bold uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark">
                        {t('systemLogs.message')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="font-mono text-xs divide-y divide-surface-border-light dark:divide-surface-border-dark">
                    {logs.map((log) => (
                      <tr
                        key={log.id}
                        className="hover:bg-surface-2-light dark:hover:bg-surface-2-dark"
                      >
                        <td className="p-4 text-text-subtle-light dark:text-text-subtle-dark whitespace-nowrap">
                          {formatTimestamp(log.created_at)}
                        </td>
                        <td className="p-4">
                          <Badge
                            variant={toneToBadgeVariant[getLogLevelTone(log.level)]}
                            className="text-[9px] px-1.5 py-0"
                          >
                            {t(`systemLogs.levels.${log.level.toUpperCase()}`, {
                              defaultValue: log.level,
                            })}
                          </Badge>
                        </td>
                        <td className="p-4 text-brand-600 dark:text-brand-400 font-bold uppercase tracking-tighter">
                          {log.source}
                        </td>
                        <td className="p-4 text-slate-700 dark:text-slate-300 break-all">
                          {translateKnownMessage(
                            log.message,
                            t,
                            i18n.resolvedLanguage || i18n.language
                          )}
                        </td>
                      </tr>
                    ))}
                    {logs.length === 0 && (
                      <tr>
                        <td
                          colSpan={4}
                          className="p-12 text-center text-text-subtle-light dark:text-text-subtle-dark font-medium"
                        >
                          {t('systemLogs.empty')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="space-y-3 lg:hidden">
              {logs.length === 0 ? (
                <Card className="p-6 text-center text-text-muted-light dark:text-text-muted-dark">
                  {t('systemLogs.empty')}
                </Card>
              ) : (
                logs.map((log) => (
                  <Card key={log.id} className="space-y-3 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <Badge variant={toneToBadgeVariant[getLogLevelTone(log.level)]}>
                        {t(`systemLogs.levels.${log.level.toUpperCase()}`, {
                          defaultValue: log.level,
                        })}
                      </Badge>
                      <time
                        dateTime={log.created_at}
                        className="text-right text-xs text-text-subtle-light dark:text-text-subtle-dark"
                      >
                        {formatTimestamp(log.created_at)}
                      </time>
                    </div>
                    <p className="break-all text-xs font-bold uppercase tracking-wide text-brand-600 dark:text-brand-400">
                      {log.source}
                    </p>
                    <p className="break-words text-sm text-text-primary-light dark:text-text-primary-dark">
                      {translateKnownMessage(
                        log.message,
                        t,
                        i18n.resolvedLanguage || i18n.language
                      )}
                    </p>
                  </Card>
                ))
              )}
            </div>

            {/* Pagination Controls */}
            <div className="flex items-center justify-between p-4 bg-surface-2-light dark:bg-surface-2-dark rounded-2xl border border-surface-border-light dark:border-surface-border-dark">
              <div className="text-xs font-bold text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-widest">
                {t('systemLogs.page', { page, total: totalPages })}
              </div>
              <nav className="flex gap-2" aria-label={t('systemLogs.pagination')}>
                <Button
                  variant="secondary"
                  className="min-h-11"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  aria-label={t('systemLogs.previousPage')}
                >
                  <ChevronLeft aria-hidden="true" size={16} /> {t('systemLogs.previous')}
                </Button>
                <Button
                  variant="secondary"
                  className="min-h-11"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page >= totalPages}
                  aria-label={t('systemLogs.nextPage')}
                >
                  {t('systemLogs.next')} <ChevronRight aria-hidden="true" size={16} />
                </Button>
              </nav>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
